import { useEffect, useState } from 'react'
import {
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type Query,
} from 'firebase/firestore'

export interface QueryResult<T> {
  data: T[]
  loading: boolean
  error: Error | null
  /** True när datan kommer från den lokala cachen, dvs. vi är troligen offline. */
  fromCache: boolean
}

/**
 * Prenumererar på en Firestore-fråga.
 *
 * Frågan MÅSTE vara memoiserad av anroparen (useMemo) – ett nytt Query-objekt
 * varje rendering skulle starta om prenumerationen i en evig loop.
 */
export function useQuerySnapshot<T>(query: Query<DocumentData> | null): QueryResult<T> {
  const [state, setState] = useState<QueryResult<T>>({
    data: [],
    loading: true,
    error: null,
    fromCache: false,
  })

  useEffect(() => {
    if (!query) {
      setState({ data: [], loading: false, error: null, fromCache: false })
      return
    }

    setState((previous) => ({ ...previous, loading: true, error: null }))

    return onSnapshot(
      query,
      { includeMetadataChanges: true },
      (snapshot) => {
        setState({
          data: snapshot.docs.map((document) => ({
            id: document.id,
            ...document.data(),
          })) as T[],
          loading: false,
          error: null,
          fromCache: snapshot.metadata.fromCache,
        })
      },
      (error) => {
        // En misslyckad fråga ser annars exakt ut som ett tomt resultat, och
        // det vanligaste felet – ett saknat sammansatt index – märks inte
        // förrän någon undrar var datan tog vägen. Loggen innehåller en
        // färdig länk för att skapa indexet.
        console.error('[Lira] Firestore-frågan misslyckades:', error.message)
        setState({ data: [], loading: false, error, fromCache: false })
      },
    )
  }, [query])

  return state
}

/** Samma sak fast för ett enskilt dokument. Referensen ska vara memoiserad. */
export function useDocSnapshot<T>(reference: DocumentReference<DocumentData> | null) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!reference) {
      setData(null)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    return onSnapshot(
      reference,
      (snapshot) => {
        setData(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as T) : null)
        setError(null)
        setLoading(false)
      },
      // Felet sväljdes tidigare, vilket gjorde att ett nekat läsförsök såg
      // likadant ut som ett dokument som inte finns. "Beställningen finns
      // inte" när sanningen är "du får inte läsa den" skickar folk åt fel
      // håll när de felsöker.
      (caught) => {
        setError(caught)
        setLoading(false)
      },
    )
  }, [reference])

  return { data, loading, error }
}
